<!--評分頁面-->
<template>
    <div class="feedback-form-container">
        <div class="logo-container">
            <img class="logo" src="@shared-assets/images/logos/Vurbo.ai Logo 2.svg" alt="Vurbo.ai" />
        </div>
        <div class="feedback-card">
            <!--希望有幫助！-->
            <h2 class="thank-you-message">
                {{ t('Components.MeetingEnd.FeedbackForm.ThankYouMessage') }}
            </h2>
            <!--即時翻譯現已結束。-->
            <p class="session-closed-message">
                {{ t('Components.MeetingEnd.FeedbackForm.SessionClosedMessage') }}
            </p>
            <div class="feedback-section">
                <!--翻譯品質如何？-->
                <p class="feedback-question">
                    {{ t('Components.MeetingEnd.FeedbackForm.FeedbackQuestion') }}
                </p>
                <div class="star-rating"
                     @mouseenter="handleStarOnMouseEnter"
                     @mouseleave="handleStarOnMouseLeave">
                    <!--星星-->
                    <button class="star-button" :class="{ active: star <= rating }"
                            v-for="star in 5" :key="star" type="button"
                            :aria-label="String(star)"
                            @click="handleStarButtonOnClick(star)">
                        <span class="star-icon" :class="{ filled: star <= rating }">★</span>
                    </button>
                </div>
                <!--提交按鈕-->
                <div class="button-container" v-if="rating > 0">
                    <button class="submit-button" type="button"
                            @click="handleSubmitButtonOnClick">
                        {{ t('Submit') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'
import { isEmpty } from '@ipevo-rd/service-web-utils'
import { useI18n } from 'vue-i18n'

export default defineComponent({
    name: 'FeedbackForm',
    emits: ['submit'],
    setup(_, { emit }) {
        // i18n
        const { t } = useI18n()


        //5秒自動跳轉
        const remainingTime = 5000
        // 重定向計時器
        const redirectTimer = ref<null | number>(null)
        // 是否暫停
        const isPaused = ref(false)
        // 星星評分
        const rating = ref(0)


        /**
         * 清除重定向計時器
         * @return {void}
         */
        const clearRedirectTimer = (): void => {
            // 如果重定向計時器不為空
            if (!isEmpty(redirectTimer.value)) {
                // 清除重定向計時器
                window.clearTimeout(redirectTimer.value!)
                // 重定向計時器設為空
                redirectTimer.value = null
            }
        }

        /**
         * 開始自動重定向計時器
         * @return {void}
         */
        const startRedirectTimer = (): void => {
            // 如果未暫停
            if (!isPaused.value) {
                // 清除重定向計時器
                clearRedirectTimer()
                // 設定重定向計時器
                redirectTimer.value = window.setTimeout(() => handleSubmitButtonOnClick(), remainingTime)
            }
        }

        /**
         * 暫停重定向
         * @return {void}
         */
        const pauseRedirect = (): void => {
            // 如果未暫停
            if (!isPaused.value) {
                //清除重定向計時器
                clearRedirectTimer()
                // 設定暫停為true
                isPaused.value = true
            }
        }

        /**
         * 恢復重定向
         * @return {void}
         */
        const resumeRedirect = (): void => {
            // 如果暫停時
            if (isPaused.value) {
                // 將暫停設為false
                isPaused.value = false
                // 開始重定向計時器
                startRedirectTimer()
            }
        }

        /**
         * 設定評分
         * @param value {number}評分值
         * @return {void}
         */
        const handleStarButtonOnClick = (value: number): void => {
            rating.value = value
        }

        /**
         * 提交評分給父元件
         * @return {void}
         */
        const handleSubmitButtonOnClick = (): void => emit('submit', rating.value)

        /**
         * 當滑鼠進入星星區域時暫停
         * @return {void}
         */
        const handleStarOnMouseEnter = (): void => pauseRedirect()

        /**
         * 當滑鼠離開星星區域時恢復
         * @return {void}
         */
        const handleStarOnMouseLeave = (): void => resumeRedirect()


        /**
         * 組件掛載時
         * @return {void}
         */
        onMounted(() => startRedirectTimer())
        /**
         * 組件卸載時
         * @return {void}
         */
        onUnmounted(() => clearRedirectTimer())


        return {
            t,
            rating,
            handleSubmitButtonOnClick,
            handleStarOnMouseEnter,
            handleStarOnMouseLeave,
            handleStarButtonOnClick,
        }
    },
})
</script>
<style lang="scss" scoped>
@use '@shared-assets/scss/variables' as *;

.feedback-form-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;

    .logo-container {
        text-align: center;
        margin-bottom: 30px;

        .logo {
            width: 200px;
            margin: 0 auto 10px;
        }
    }

    .feedback-card {
        max-width: 500px;
        width: 100%;
        border-radius: 16px;
        background: transparent;

        .thank-you-message {
            color: var(--ui-grey-darkest);
            font-size: 24px;
            font-weight: 600;
            margin: 0 0 8px;
            text-align: center;
        }

        .session-closed-message {
            color: var(--ui-grey-darkest);
            font-size: 18px;
            margin-top: 0;
            text-align: center;
        }

        .feedback-section {
            margin-top: 30px;
        }

        .feedback-question {
            color: var(--ui-grey-darkest);
            font-size: 18px;
            margin-bottom: 15px;
            text-align: center;
        }

        .star-rating {
            display: flex;
            justify-content: center;
            margin-bottom: 20px;

            .star-button {
                background: transparent;
                border: none;
                padding: 0 5px;
                cursor: pointer;
                transition: transform 0.2s ease;

                &.active {
                    transform: scale(1.1);
                }

                .star-icon {
                    font-size: 24px;
                    color: #ddd;
                    transition: color 0.2s ease;

                    &.filled {
                        color: var(--ui-ignite);
                    }
                }
            }
        }

        .button-container {
            display: flex;
            justify-content: center;
            margin-top: 20px;

            .submit-button {
                border-radius: 24px;
                background: var(--ui-ignite);
                color: #fff;
                font-weight: 500;
                font-size: 16px;
                height: 48px;
                max-width: 200px;
                width: 100%;
                border: none;
                cursor: pointer;
            }
        }
    }
}

@media (max-width: #{$breakpoint-sm - 1}) {
    .feedback-form-container {
        .logo-container .logo {
            width: 150px;
        }

        .feedback-card {
            .thank-you-message {
                font-size: 20px;
            }

            .session-closed-message {
                font-size: 16px;
            }

            .feedback-question {
                font-size: 16px;
            }

            .button-container .submit-button {
                font-size: 14px;
                height: 44px;
            }
        }
    }
}
</style>
